import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Image from "global/Image";
import uploadImageUtils from "utils/uploadImage";
import ImageProfile from "./ImageProfile";
import ComposeModalCroppingImage from "./ComposeModalCroppingImage";
import OnBoardingWrapper from "../styled";
import { UploadProfilePictureWrapper, CustomSpinner } from "./styled";
import {
  effects as effectsProfile,
  selectors as selectorsProfile,
} from "lib/entities/profile";
import { effects as effectsOnboarding } from "lib/entities/onboarding";
import {
  effects as effectsUploadImage,
  selectors as selectorUploadImage,
} from "./slice";

import handleErrorMessageAPI from "global/AlertErrorMessage";
import SUCCESS_MESSAGE from "constants/successMessage";
import ERROR_MESSAGE from "constants/errorMessage";
import { AWS_AVATAR_BUCKET } from "constants/api";

function UploadProfilePicture() {
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imageCropBuffer, setImageCropBuffer] = useState(null);
  const [imageInput, setImageInput] = useState({});
  const [imageCropBufferReady, setImageCropBufferReady] = useState(null);
  const [showModalCroppingImage, setShowModalCroppingImage] = useState(false);
  const [outputFormat, setOutputFormat] = useState("jpg");

  const { data: dataProfile, isLoading: isLoadingProfile } =
    useSelector(selectorsProfile);

  const { uploadToken } = useSelector(selectorUploadImage);
  const { data } = uploadToken;

  const dispatch = useDispatch();

  const { username, onboarding: progress, avatar } = dataProfile;

  useEffect(() => {
    setAvatarLoading(isLoadingProfile);
  }, [isLoadingProfile]);

  /**
   * isNoImageAvatar
   * to check no image avatar from API
   * @param {string} imageURL
   * @return {boolean}
   */
  const isNoImageAvatar = (imageURL) => {
    if (!imageURL) return false;
    return (
      imageURL === "0" ||
      imageURL === "noimage.jpg" ||
      imageURL.indexOf("noimage.png") > -1
    );
  };

  /**
   * Handling intention to change avatar by linking the event to hidden image
   * input click
   */
  const handleChangeAvatar = (event) => {
    event.preventDefault(); // prevent default
    event.stopPropagation();

    imageInput.click(); // Channel click event
  };

  const handleNextStep = (event) => {
    if (
      event.type === "click" ||
      (event.type === "keydown" && (event.key === "Enter" || event.key === " "))
    ) {
      const nextStep = progress + 1;
      dispatch(effectsOnboarding.userSkipOnboarding(parseInt(nextStep, 10)));
    }
  };

  const handleAvatarLoad = () => {
    const imagefile = document.getElementById("hide_avatar");
    const oldCrop = document.getElementById("modal-cropping"); // use element canvas id #canvas-editor Component Landing

    if (typeof oldCrop !== "undefined" && oldCrop != null) {
      // destroy canvas
      oldCrop.style.display = "none";
    }

    // Process to fix image ratio
    const canvas = document.getElementById("canvas-editor"); // use element canvas id #canvas-editor Component Landing
    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    let { width, height } = imagefile;
    const MAX_WIDTH = 0.6 * window.screen.availWidth; // 70 %
    const MAX_HEIGHT = 0.6 * window.screen.availHeight; // 70 %

    if (width > height) {
      // landscape
      if (width > MAX_WIDTH) {
        height *= MAX_WIDTH / width;
        width = MAX_WIDTH;
      }
    } else if (height > MAX_HEIGHT) {
      width *= MAX_HEIGHT / height;
      height = MAX_HEIGHT;
    }

    width = Math.round(width);
    height = Math.round(height);

    canvas.width = width;
    canvas.height = height;

    ctx.drawImage(imagefile, 0, 0, width, height);

    const dataurl = canvas.toDataURL("image/png");

    // trigger component ComposeModalCroppingImage to open
    setImageCropBufferReady(dataurl);
    setShowModalCroppingImage(true);
  };

  /**
   * handleImageChange
   * Handling image input value change
   */
  const handleImageChange = (event) => {
    event.preventDefault();

    const reader = new FileReader();
    const file = event.target.files[0]; // Avatar ImageFile object
    let fileOutputFormat = "jpg";

    if (!file) return false;
    if (file.length < 1) return false;

    // Only process image files type: JPEG, JPG, PNG, and GIF.
    if (
      !file.type.match("image.jpeg") &&
      !file.type.match("image.jpg") &&
      !file.type.match("image.png") &&
      !file.type.match("image.gif")
    ) {
      handleErrorMessageAPI(
        ERROR_MESSAGE.UPLOAD_IMAGE_TYPE_NOT_SUPPORTED,
        ERROR_MESSAGE.ALERT_RED,
      );
      return false;
    }

    // Max size 1 MB = 1048576 bytes.
    // if (file.size > 1048576) {
    if (file.size > 2097152) {
      handleErrorMessageAPI(
        ERROR_MESSAGE.MAX_FILE_SIZE,
        ERROR_MESSAGE.ALERT_RED,
      );
      return false;
    }

    // Clean imageCropBuffer dom for other/same file upload trial
    document.querySelector("input.fileInput").value = "";

    // Closure to capture the file information.
    reader.onloadend = () => {
      if (file.type === "image/png") {
        fileOutputFormat = "png";
      }

      setImageCropBuffer(reader.result);
      setOutputFormat(fileOutputFormat);
    };

    // Read in the image file as a data URL.
    reader.readAsDataURL(file);

    return true;
  };

  /**
   * handleCancelModal
   * Hide crop image modal
   */
  const handleCancelModal = () => {
    // Clean imageCropBuffer dom for other/same file upload trial
    document.querySelector("input.fileInput").value = "";

    // update state
    setShowModalCroppingImage(false);
    setImageCropBuffer(null);
    setImageCropBufferReady(null);
  };

  /**
   * handleCroppingImage
   * Handle image crop after being submited by modal
   */
  const handleCroppingImage = (image) => {
    setAvatarLoading(true);

    // Clean imageCropBuffer dom for other/same file upload trial
    document.querySelector("input.fileInput").value = "";

    // update state
    setImagePreviewUrl(image);
    setShowModalCroppingImage(false);
    setImageCropBuffer(image);
    // handleSubmitAction();
  };

  const uploadAvatar = async (newFileName) => {
    const uploadToken = await dispatch(
      effectsUploadImage.retrieveUploadToken(newFileName),
    );

    const { error: errorUploadToken } = uploadToken.payload;
    if (errorUploadToken) {
      return;
    }

    const key = uploadToken.payload.data.fields[0].value;
    const file = uploadImageUtils.base64ToImage(imagePreviewUrl);
    const uploadImage = await dispatch(
      effectsUploadImage.uploadImageAvatar({ file, newFileName }),
    );

    const { error: errorUploadImage } = uploadImage.payload;
    if (errorUploadImage) {
      return;
    }

    if (key) {
      const editProfile = await dispatch(
        effectsProfile.editUserProfile({
          avatar: key,
        }),
      );

      const { error: errorEditProfile } = editProfile.payload;
      if (errorEditProfile) {
        return;
      } else {
        handleErrorMessageAPI(
          SUCCESS_MESSAGE.PROFILE_UPDATED,
          SUCCESS_MESSAGE.ALERT_GREEN,
        );
        setTimeout(() => {
          const nextStep = progress + 1;
          dispatch(
            effectsOnboarding.userSkipOnboarding(parseInt(nextStep, 10)),
          );
        }, 1500);
      }
    }
  };

  useEffect(() => {
    if (imagePreviewUrl) {
      const newFileName = uploadImageUtils.formatUploadImageName("jpeg");

      const sourceImage = document.querySelector(".editavatar");
      const resultImage = document.getElementById("result_image");

      if (sourceImage && sourceImage.src === "") {
        handleErrorMessageAPI(
          ERROR_MESSAGE.PLEASE_LOAD_IMAGE,
          ERROR_MESSAGE.ALERT_RED,
        );
        return false;
      }

      handleCancelModal();

      // compress image then HIT API
      uploadImageUtils
        .imageCompressor(sourceImage, outputFormat)
        .then((dataurl) => {
          resultImage.src = dataurl;

          return uploadAvatar(newFileName);
        });
    }
  }, [imagePreviewUrl]);

  // avatarContainerClassName to dynamic className
  const avatarContainerClassName = `boarding-avatar ${
    avatarLoading ? "loading" : ""
  } ${isNoImageAvatar(avatar) ? "avatar-not-set" : ""}`;

  return (
    <OnBoardingWrapper>
      <div className="boarding-cover">
        <div className="boarding-content">
          <UploadProfilePictureWrapper className="boarding-in">
            {/* start: trigger image upload */}
            {avatarLoading && (
              <div className="spinner-container">
                <CustomSpinner />
              </div>
            )}
            {!avatarLoading && (
              <div
                role="button"
                tabIndex={-1}
                className={avatarContainerClassName}
                onClick={handleChangeAvatar}
                onKeyDown={handleChangeAvatar}
              >
                <ImageProfile
                  overlayClassName="editavatar"
                  mode="BIG"
                  temp_avatar={imagePreviewUrl}
                  avatar={avatar}
                  username={username}
                  hide_loader={!avatarLoading}
                  avatarURL={AWS_AVATAR_BUCKET}
                />
              </div>
            )}

            {/* end: trigger image upload */}

            <div className="boarding-title">Upload Profile Picture</div>
            <div className="boarding-text">
              Upload your profile photo so your friend know it&apos;s you.
            </div>

            {/* start: logic button next */}
            {/* {web}/OnboardingPage/OnboardingStepOne/OnboardingStepOne.js */}
            {/* line 316 */}
            {isNoImageAvatar(avatar) ? (
              <div
                role="button"
                tabIndex={-1}
                className="boarding-button"
                onClick={handleChangeAvatar}
                onKeyDown={handleChangeAvatar}
              >
                {" "}
                Upload Photo{" "}
              </div>
            ) : (
              <div
                role="button"
                tabIndex={-1}
                className="boarding-button-next"
                onClick={handleNextStep}
                onKeyDown={handleNextStep}
              >
                Next
              </div>
            )}
            {/* end: logic button next */}

            {/* start: logic button skip */}
            {/* {web}/OnboardingPage/OnboardingStepOne/OnboardingStepOne.js */}
            {/* line 339 */}
            {isNoImageAvatar(avatar) && (
              <div
                role="button"
                tabIndex={-1}
                className="boarding-skip"
                onClick={handleNextStep}
                onKeyDown={handleNextStep}
              >
                Skip
              </div>
            )}
            {/* end: logic button skip */}

            {/* start: section image processing */}
            <section>
              <Image
                alt="hide_avatar"
                hidden
                id="hide_avatar"
                src={imageCropBuffer}
                className="hide"
                onLoad={handleAvatarLoad}
              />
              <Image
                alt="result_image"
                hidden
                id="result_image"
                className="img_container hide"
              />
              <input
                ref={(fileImage) => {
                  setImageInput(fileImage);
                }}
                className="fileInput"
                type="file"
                onChange={handleImageChange}
                style={{ display: "none" }}
              />
            </section>
            {/* end: section image processing */}

            {/* start: section canvas cropping box */}
            <section>
              <div className="cropping-box">
                <div id="compose-modal-cropping">
                  {showModalCroppingImage ? (
                    <ComposeModalCroppingImage
                      elementSelector="#compose-modal-cropping"
                      title="Cropping Image"
                      src={imageCropBufferReady}
                      onSubmitModal={handleCroppingImage}
                      onCancelModal={handleCancelModal}
                      buttonSubmitTitle="Crop & Apply"
                    />
                  ) : null}
                </div>
                <div id="canvas-editor-wrapper">
                  <canvas
                    id="canvas-editor"
                    className="hide"
                    width="400"
                    height="400"
                  />
                </div>
              </div>
            </section>
            {/* end: section canvas cropping box */}
          </UploadProfilePictureWrapper>
        </div>
      </div>
    </OnBoardingWrapper>
  );
}

export default UploadProfilePicture;
